import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import {
  Brain, Shield, Wifi, WifiOff, CheckCircle2, XCircle,
  Loader2, Server, Cloud, Lock, TestTube
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AIUsagePanel from '../../components/admin/AIUsagePanel';
import { toast } from 'sonner';

const PROVIDER_ICONS = {
  disabled: WifiOff, openai: Cloud, anthropic: Cloud, ollama: Server, azure_openai: Cloud,
};

const PlatformSettings = () => {
  const [aiConfig, setAiConfig] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('disabled');
  const [formConfig, setFormConfig] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [aiRes, depRes] = await Promise.all([
        axios.get(`${API}/settings/ai`),
        axios.get(`${API}/settings/deployment`),
      ]);
      setAiConfig(aiRes.data);
      setDeployment(depRes.data);
      setSelectedProvider(aiRes.data.provider || 'disabled');
      setFormConfig(aiRes.data.config || {});
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  // Strip read-only `*_set` boolean flags that the GET /ai response leaks
  // into form state — they are display-only and break the PUT validator.
  const sanitiseConfig = (cfg) => {
    const clean = {};
    for (const [k, v] of Object.entries(cfg || {})) {
      if (k.endsWith('_set')) continue;
      if (v === null || v === undefined) continue;
      clean[k] = typeof v === 'string' ? v : String(v);
    }
    return clean;
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/ai`, {
        provider: selectedProvider,
        config: sanitiseConfig(formConfig),
      });
      toast.success(`AI provider set to ${selectedProvider}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      // Save first, then test
      await axios.put(`${API}/settings/ai`, {
        provider: selectedProvider,
        config: sanitiseConfig(formConfig),
      });
      const res = await axios.post(`${API}/settings/ai/test`);
      setTestResult(res.data);
      res.data.status === 'success' ? toast.success('Connection successful!') : toast.error(res.data.message);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Test failed: ${detail}`);
      setTestResult({ status: 'error', message: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally { setTesting(false); }
  };

  const setField = (key, value) => setFormConfig(p => ({ ...p, [key]: value }));

  const currentProviderInfo = aiConfig?.providers?.find(p => p.id === selectedProvider);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-settings-page">
      <div>
        <h1 className="text-2xl font-bold font-['Chivo']">Platform Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure AI providers and deployment mode</p>
      </div>

      {/* Deployment Mode */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4 text-primary" />Deployment Mode</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {deployment?.available_modes?.map(m => (
              <div key={m.id} className={`p-4 rounded-lg border-2 ${deployment.mode === m.id ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <p className="text-sm font-semibold">{m.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.description}</p>
                <div className="mt-3 space-y-1">
                  <p className="text-[10px] text-muted-foreground">AI: {m.ai_options.join(', ')}</p>
                  <p className="text-[10px] text-muted-foreground">Auth: {m.auth_options.join(', ')}</p>
                </div>
                {deployment.mode === m.id && <Badge className="mt-2 text-[10px]">Current</Badge>}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Set <code className="bg-secondary px-1 rounded">DEPLOYMENT_MODE={'{saas|enterprise|airgap}'}</code> in backend environment to change.</p>
        </CardContent>
      </Card>

      {/* AI Provider Selection */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Brain className="w-4 h-4 text-primary" />AI Provider</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Provider cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {aiConfig?.providers?.map(p => {
              const Icon = PROVIDER_ICONS[p.id] || Cloud;
              const isSelected = selectedProvider === p.id;
              return (
                <div key={p.id} onClick={() => setSelectedProvider(p.id)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}
                  data-testid={`provider-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{p.label}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{p.description}</p>
                  <div className="mt-2">
                    {p.data_external ? (
                      <Badge variant="outline" className="text-[9px] text-amber-400 border-amber-400/30">Data leaves network</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px] text-green-400 border-green-400/30"><Lock className="w-2.5 h-2.5 mr-1" />Data stays internal</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Provider-specific config */}
          {selectedProvider === 'openai' && (
            <div className="space-y-3 p-4 bg-secondary/30 rounded-lg border border-border">
              <div><Label>OpenAI API Key</Label><Input type="password" value={formConfig.openai_key || ''} onChange={e => setField('openai_key', e.target.value)} placeholder="sk-..." className="bg-secondary border-border mt-1 font-mono" /></div>
              <div><Label>Model</Label>
                <Select value={formConfig.openai_model || 'gpt-4o'} onValueChange={v => setField('openai_model', v)}>
                  <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{currentProviderInfo?.models?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {selectedProvider === 'anthropic' && (
            <div className="space-y-3 p-4 bg-secondary/30 rounded-lg border border-border">
              <div><Label>Anthropic API Key</Label><Input type="password" value={formConfig.anthropic_key || ''} onChange={e => setField('anthropic_key', e.target.value)} placeholder="sk-ant-..." className="bg-secondary border-border mt-1 font-mono" /></div>
              <div><Label>Model</Label>
                <Select value={formConfig.anthropic_model || 'claude-3-5-sonnet-20241022'} onValueChange={v => setField('anthropic_model', v)}>
                  <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{currentProviderInfo?.models?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {selectedProvider === 'ollama' && (
            <div className="space-y-3 p-4 bg-secondary/30 rounded-lg border border-border">
              <div className="flex items-start gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg mb-2">
                <Lock className="w-4 h-4 text-green-400 mt-0.5" />
                <p className="text-xs text-green-400">Your data never leaves your network with Ollama. All processing happens on your server.</p>
              </div>
              <div><Label>Ollama Server URL</Label><Input value={formConfig.ollama_url || 'http://localhost:11434'} onChange={e => setField('ollama_url', e.target.value)} placeholder="http://192.168.1.50:11434" className="bg-secondary border-border mt-1 font-mono" /></div>
              <div><Label>Model</Label>
                <Select value={formConfig.ollama_model || 'llama3'} onValueChange={v => setField('ollama_model', v)}>
                  <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{currentProviderInfo?.models?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {selectedProvider === 'azure_openai' && (
            <div className="space-y-3 p-4 bg-secondary/30 rounded-lg border border-border">
              <div className="flex items-start gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg mb-2">
                <Lock className="w-4 h-4 text-green-400 mt-0.5" />
                <p className="text-xs text-green-400">Data stays within your Azure tenant. No data goes to public OpenAI.</p>
              </div>
              <div><Label>Azure OpenAI Endpoint</Label><Input value={formConfig.azure_openai_endpoint || ''} onChange={e => setField('azure_openai_endpoint', e.target.value)} placeholder="https://your-resource.openai.azure.com" className="bg-secondary border-border mt-1 font-mono" /></div>
              <div><Label>API Key</Label><Input type="password" value={formConfig.azure_openai_key || ''} onChange={e => setField('azure_openai_key', e.target.value)} className="bg-secondary border-border mt-1 font-mono" /></div>
              <div><Label>Deployment Name</Label>
                <Select value={formConfig.azure_openai_deployment || 'gpt-4o'} onValueChange={v => setField('azure_openai_deployment', v)}>
                  <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{currentProviderInfo?.models?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={saveConfig} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Save Configuration</Button>
            {selectedProvider !== 'disabled' && (
              <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="test-ai-btn">
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}Test Connection
              </Button>
            )}
          </div>

          {/* Test result */}
          {testResult && (
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${testResult.status === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
              {testResult.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 mt-0.5" />}
              <div>
                <p className="text-xs font-medium">{testResult.status === 'success' ? 'Connection successful!' : 'Connection failed'}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{testResult.message?.substring(0, 200)}</p>
                {testResult.provider && <p className="text-[10px] text-muted-foreground mt-1">Provider: {testResult.provider} | Model: {testResult.model}</p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Usage & Health */}
      <AIUsagePanel />
    </div>
  );
};

export default PlatformSettings;
